import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import UniqueEntityID from '../../../../core/domain/unique_entity_id';
import {
  Cache,
  CachedMessage,
} from '../../../../data/dietitian/domain/cache/cache';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { DietitianCommands } from '../../../../data/dietitian/domain/dietitian_commands';
import { DietitianStateProvider } from '../../../../data/dietitian/domain/dietitian_state_provider';
import { ConversationProps } from '../../../../data/message/domain/conversation/conversation';
import { ConversationCommands } from '../../../../data/message/domain/conversation/conversation_commands';
import { MessageProps } from '../../../../data/message/domain/message/message';
import { MessageCommands } from '../../../../data/message/domain/message/message_commands';
import { Patient } from '../../../../data/patient/domain/patient';
import { PatientCommands } from '../../../../data/patient/domain/patient_commands';
import { MyDietitianStateProvider } from '../../../my/my-dietitian-state.provider';

export interface MessageData {
  senderId: UniqueEntityID;
  conversationProps: ConversationProps;
  recipientId: UniqueEntityID;
  conversationId: UniqueEntityID;
  userId: UniqueEntityID;
}

export interface MessageViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

@Injectable()
export class ConversationFacade {
  constructor(
    private conversationCommands: ConversationCommands,
    private dietitianCommands: DietitianCommands,
    private patientCommands: PatientCommands,
    private messageCommands: MessageCommands,
    private dietitianStateProvider: DietitianStateProvider,
    private myDietitianStateProvider: MyDietitianStateProvider,
  ) {
    // do nothing
  }

  get dietitianState$(): Observable<MessageViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <MessageViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  get myDietitianState$(): Observable<MessageViewModel> {
    return this.myDietitianStateProvider.state$.pipe(
      map((e) => {
        return <MessageViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  loadPatient(patientId: string) {
    return this.patientCommands.getPatient(patientId);
  }

  saveConversation(
    props: ConversationProps,
    messageId: string,
    notify?: boolean,
    markAsRead?: boolean,
  ) {
    return this.conversationCommands.save(props, messageId, notify, markAsRead);
  }

  loadConversation(messageId: string) {
    try {
      return this.conversationCommands.findConversation(messageId);
    } catch (e) {
      console.log('❌ loadConversation', e);
      throw e;
    }
  }

  async sendAudio(data: MessageData, audioBlob: Blob) {
    const uploadResult = await this.messageCommands.uploadAudio(
      data.conversationId.toString(),
      data.userId.toString(),
      audioBlob,
    );

    const props = {
      createdAt: new Date(),
      mimeType: 'audio/*',
      resourcePath: uploadResult.path,
      url: uploadResult.url,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  async sendPicture(data: MessageData, imageUrl: string) {
    const uploadResult = await this.messageCommands.uploadPicture(
      data.conversationId.toString(),
      data.userId.toString(),
      imageUrl,
    );

    const props = {
      createdAt: new Date(),
      mimeType: 'image/*',
      resourcePath: uploadResult.path,
      url: uploadResult.url,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  async sendMessage(data: MessageData, messageContent: string) {
    const props = {
      createdAt: new Date(),
      message: messageContent,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  private async send(data: MessageData, props: MessageProps) {
    const message = await this.messageCommands.save(
      data.conversationId.toString(),
      props,
    );

    this.saveConversation(
      data.conversationProps,
      data.conversationId.toString(),
    );

    return message;
  }

  listenMessages(messageId: string, dietitian: Dietitian, patient: Patient) {
    return this.messageCommands.listen(messageId, dietitian, patient);
  }

  async loadCachedMessage(
    dietitianId: string | undefined,
    patientId: string | undefined,
    conversationId: string | undefined,
  ): Promise<CachedMessage | undefined> {
    if (dietitianId && patientId && conversationId) {
      const cache = await this.dietitianCommands.loadCache(
        dietitianId,
        patientId,
      );

      if (cache) {
        const expectedMessages = cache.cachedMessages?.filter(
          (message) => message.conversationId === conversationId,
        );

        if (expectedMessages && expectedMessages.length === 1) {
          return expectedMessages[0];
        }
      }
    }
    return undefined;
  }

  async cacheMessage(
    dietitianId: string | undefined,
    patientId: string | undefined,
    conversationId: string | undefined,
    value: string | undefined,
  ): Promise<Cache | undefined> {
    if (dietitianId && patientId && conversationId) {
      const cache = await this.dietitianCommands.loadCache(
        dietitianId,
        patientId,
      );
      if (cache) {
        const expectedMessages = cache.cachedMessages?.filter(
          (message) => message.conversationId === conversationId,
        );

        if (expectedMessages && expectedMessages.length === 1) {
          expectedMessages[0].message = value;

          for (let i = cache.cachedMessages!.length - 1; i >= 0; i--) {
            if (
              cache.cachedMessages![i].message === undefined ||
              cache.cachedMessages![i].message == ''
            ) {
              cache.cachedMessages!.splice(i, 1);
            }
          }

          return this.dietitianCommands.saveCache(cache);
        } else {
          if (!cache.cachedMessages) {
            cache.cachedMessages = [];
          }
          cache.cachedMessages.push({
            message: value,
            conversationId: conversationId,
          });
          return this.dietitianCommands.saveCache(cache);
        }
      } else {
        const cache = Cache.create({
          dietitianId,
          patientId,
          cachedMessages: [
            {
              message: value,
              conversationId: conversationId,
            },
          ],
        });
        return this.dietitianCommands.saveCache(cache);
      }
    }
    return undefined;
  }
}
